import { Component, Input, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { SupabaseService, ProductReview, ProductReviewInput } from '../../services/supabase';
import { CartService } from '../../services/cart';

@Component({
  selector: 'app-product-reviews',
  standalone: true,
  imports: [CommonModule, DecimalPipe, FormsModule, ReactiveFormsModule],
  templateUrl: './product-reviews.html',
  styleUrl: './product-reviews.scss'
})
export class ProductReviews implements OnInit {
  private supabase = inject(SupabaseService);
  private fb = inject(FormBuilder);
  cartService = inject(CartService);

  @Input({ required: true }) productSlug!: string;
  @Input() productName: string = 'Este Espirituoso';
  @Input() productId?: string;

  reviews = signal<ProductReview[]>([]);
  isLoading = signal<boolean>(true);
  isFormOpen = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);
  successToast = signal<string | null>(null);

  // Descuento del 10% por opinión
  unlockedDiscount = signal<{ code: string; percent: number; isApplied: boolean } | null>(null);
  isCopied = signal<boolean>(false);

  // Filtros y ordenamiento
  selectedStarFilter = signal<number | null>(null);
  selectedSort = signal<'recent' | 'highest' | 'helpful'>('recent');

  // Formulario interactivo
  hoverRating = signal<number>(0);
  reviewForm = this.fb.group({
    rating: [5, [Validators.required, Validators.min(1), Validators.max(5)]],
    customer_name: ['', [Validators.required, Validators.minLength(2)]],
    customer_city: [''],
    title: ['', [Validators.required, Validators.minLength(3)]],
    comment: ['', [Validators.required, Validators.minLength(10)]]
  });

  // Métricas calculadas
  totalReviews = computed(() => this.reviews().length);

  averageRating = computed(() => {
    const list = this.reviews();
    if (list.length === 0) return 5.0;
    const sum = list.reduce((acc, r) => acc + r.rating, 0);
    return Math.round((sum / list.length) * 10) / 10;
  });

  recommendationPercentage = computed(() => {
    const list = this.reviews();
    if (list.length === 0) return 100;
    const positiveCount = list.filter(r => r.rating >= 4).length;
    return Math.round((positiveCount / list.length) * 100);
  });

  starDistribution = computed(() => {
    const list = this.reviews();
    const total = list.length || 1;
    const counts: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    list.forEach(r => {
      const star = Math.min(5, Math.max(1, r.rating));
      counts[star] = (counts[star] || 0) + 1;
    });

    return [5, 4, 3, 2, 1].map(star => ({
      star,
      count: counts[star] || 0,
      percentage: Math.round(((counts[star] || 0) / total) * 100)
    }));
  });

  filteredReviews = computed(() => {
    let list = [...this.reviews()];
    const starFilter = this.selectedStarFilter();
    if (starFilter !== null) {
      list = list.filter(r => r.rating === starFilter);
    }

    const sort = this.selectedSort();
    if (sort === 'highest') {
      list.sort((a, b) => b.rating - a.rating);
    } else if (sort === 'helpful') {
      list.sort((a, b) => (b.helpful_count || 0) - (a.helpful_count || 0));
    } else {
      // Recent
      list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    return list;
  });

  // Lista de votos dados en sesión para prevenir spam
  votedReviewIds = signal<Set<string>>(new Set());

  async ngOnInit(): Promise<void> {
    await this.loadReviews();
  }

  async loadReviews(): Promise<void> {
    this.isLoading.set(true);
    try {
      const data = await this.supabase.getProductReviews(this.productSlug);
      this.reviews.set(data);
    } catch (err) {
      console.error('Error al cargar opiniones:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  toggleForm(): void {
    this.isFormOpen.update(v => !v);
  }

  setRating(star: number): void {
    this.reviewForm.patchValue({ rating: star });
  }

  setHoverRating(star: number): void {
    this.hoverRating.set(star);
  }

  clearHoverRating(): void {
    this.hoverRating.set(0);
  }

  getStarRatingText(rating: number): string {
    switch (rating) {
      case 5: return 'Excepcional — 5 Estrellas';
      case 4: return 'Muy Bueno — 4 Estrellas';
      case 3: return 'Bueno — 3 Estrellas';
      case 2: return 'Regular — 2 Estrellas';
      case 1: return 'Mejorable — 1 Estrella';
      default: return 'Selecciona tu puntuación';
    }
  }

  filterByStar(star: number | null): void {
    this.selectedStarFilter.set(this.selectedStarFilter() === star ? null : star);
  }

  setSort(sort: 'recent' | 'highest' | 'helpful'): void {
    this.selectedSort.set(sort);
  }

  async submitReview(): Promise<void> {
    if (this.reviewForm.invalid) {
      this.reviewForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    try {
      const formVal = this.reviewForm.value;
      const input: ProductReviewInput = {
        product_id: this.productId,
        product_slug: this.productSlug,
        customer_name: formVal.customer_name || 'Catador Anónimo',
        customer_city: formVal.customer_city || 'Ecuador',
        rating: formVal.rating || 5,
        title: formVal.title || 'Excelente destilado',
        comment: formVal.comment || ''
      };

      const newReview = await this.supabase.submitProductReview(input);

      // Agregar de inmediato a la lista reactiva al principio
      this.reviews.update(current => [newReview, ...current]);

      // Aplicar y desbloquear el descuento del 10%
      const discountCode = 'CATADOR10';
      this.cartService.applyCoupon(discountCode, 10);
      this.unlockedDiscount.set({
        code: discountCode,
        percent: 10,
        isApplied: true
      });

      // Limpiar formulario y cerrar panel de edición
      this.reviewForm.reset({ rating: 5 });
      this.isFormOpen.set(false);
      this.showToast('¡Gracias por tu opinión! Has obtenido un 10% de descuento en tu compra.');
    } catch (err) {
      console.error('Error al registrar la opinión:', err);
      this.showToast('Ocurrió un error al guardar tu opinión. Inténtalo nuevamente.');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async copyDiscountCode(): Promise<void> {
    const code = this.unlockedDiscount()?.code || 'CATADOR10';
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(code);
      }
      this.isCopied.set(true);
      this.showToast(`¡Código ${code} copiado al portapapeles!`);
      setTimeout(() => this.isCopied.set(false), 3000);
    } catch {
      this.showToast(`Cupón: ${code}`);
    }
  }

  dismissDiscount(): void {
    this.unlockedDiscount.set(null);
  }

  async voteHelpful(review: ProductReview): Promise<void> {
    if (this.votedReviewIds().has(review.id)) return;

    this.votedReviewIds.update(set => new Set(set).add(review.id));
    const newCount = await this.supabase.voteReviewHelpful(review.id, review.helpful_count || 0);

    this.reviews.update(list =>
      list.map(r => (r.id === review.id ? { ...r, helpful_count: newCount } : r))
    );
  }

  getInitials(name: string): string {
    if (!name) return 'ED';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  formatDate(dateStr: string): string {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('es-EC', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Reciente';
    }
  }

  getStarsArray(count: number): number[] {
    return Array.from({ length: 5 }, (_, i) => i + 1);
  }

  private showToast(msg: string): void {
    this.successToast.set(msg);
    setTimeout(() => {
      this.successToast.set(null);
    }, 4500);
  }
}
